import { z } from "zod";
import { shippingStatusSchema } from "./checkout.js";

export const SHIPPING_PROVINCES = [
  "北京市",
  "天津市",
  "河北省",
  "山西省",
  "内蒙古自治区",
  "辽宁省",
  "吉林省",
  "黑龙江省",
  "上海市",
  "江苏省",
  "浙江省",
  "安徽省",
  "福建省",
  "江西省",
  "山东省",
  "河南省",
  "湖北省",
  "湖南省",
  "广东省",
  "广西壮族自治区",
  "海南省",
  "重庆市",
  "四川省",
  "贵州省",
  "云南省",
  "西藏自治区",
  "陕西省",
  "甘肃省",
  "青海省",
  "宁夏回族自治区",
  "新疆维吾尔自治区",
  "香港特别行政区",
  "澳门特别行政区",
  "台湾省",
] as const;
export const shippingProvinceSchema = z.enum(SHIPPING_PROVINCES);
const fee = z.int().min(0).max(100_000_000);
export const shippingRuleSchema = z.discriminatedUnion("mode", [
  z.strictObject({ mode: z.literal("free") }),
  z.strictObject({ mode: z.literal("flat"), feeFen: fee }),
  z.strictObject({ mode: z.literal("threshold"), feeFen: fee, thresholdFen: fee.min(1) }),
  z.strictObject({ mode: z.literal("blocked") }),
]);
export const shippingConfigSchema = z
  .strictObject({
    enabled: z.boolean(),
    regions: z.array(shippingProvinceSchema).max(34),
    defaultRule: shippingRuleSchema,
    overrides: z
      .array(z.strictObject({ province: shippingProvinceSchema, rule: shippingRuleSchema }))
      .max(34),
  })
  .superRefine((value, ctx) => {
    if (value.enabled && value.regions.length === 0)
      ctx.addIssue({ code: "custom", path: ["regions"], message: "启用前请选择配送范围" });
    if (new Set(value.regions).size !== value.regions.length)
      ctx.addIssue({ code: "custom", path: ["regions"], message: "配送地区不能重复" });
    const seen = new Set<string>();
    value.overrides.forEach((item, index) => {
      if (seen.has(item.province) || !value.regions.includes(item.province))
        ctx.addIssue({
          code: "custom",
          path: ["overrides", index, "province"],
          message: "例外地区须在配送范围内且不能重复",
        });
      seen.add(item.province);
    });
  });
export const shippingPolicySchema = z.object({
  revision: z.int().min(0),
  config: shippingConfigSchema.nullable(),
});
export const saveShippingPolicySchema = z.strictObject({
  expectedRevision: z.int().min(0).max(2_147_483_646),
  config: shippingConfigSchema,
});
/** 运费金额的判定依据。订单会冻结下单当时的值，便于事后解释「为什么是这个数」。 */
export const shippingReasonSchema = z.enum([
  "ADDRESS_REQUIRED",
  "POLICY_DISABLED",
  "UNKNOWN_REGION",
  "NOT_DELIVERABLE",
  "FREE",
  "FLAT",
  "BELOW_THRESHOLD",
  "THRESHOLD_REACHED",
]);
export const shippingQuoteSchema = z
  .object({
    status: shippingStatusSchema,
    feeFen: z.int().nonnegative().nullable(),
    reason: shippingReasonSchema,
    policyRevision: z.int().positive().nullable(),
  })
  .refine(
    (value) => (value.status === "READY") === (value.feeFen !== null),
    "运费状态与金额不一致",
  );
export const shippingPreviewRequestSchema = z.strictObject({
  config: shippingConfigSchema,
  province: z.string().max(64),
  materialFen: z.int().min(0).max(1_000_000_000),
});
export type ShippingRule = z.infer<typeof shippingRuleSchema>;
export type ShippingConfig = z.infer<typeof shippingConfigSchema>;
export type ShippingPolicy = z.infer<typeof shippingPolicySchema>;
export type SaveShippingPolicy = z.infer<typeof saveShippingPolicySchema>;
export type ShippingQuote = z.infer<typeof shippingQuoteSchema>;
export type ShippingPreviewRequest = z.infer<typeof shippingPreviewRequestSchema>;
